/**
 * Release notes – update when shipping a new version.
 * Add a new entry to RELEASE_NOTES and put the latest version first.
 *
 * Use `sections` for grouped notes (New features, Updates, …).
 * Use `changes` for a flat list (legacy / older entries).
 *
 * Item text may use a small subset of HTML for emphasis, e.g. `<b>`, `<i>`, `<em>`, `<strong>`.
 */
export interface ReleaseNoteSection {
  title: string;
  items: string[];
}

export interface ReleaseNoteItem {
  version: string;
  date?: string;
  /** Flat list when not using `sections`. */
  changes?: string[];
  /** Grouped changelog (headings + bullet lists). */
  sections?: ReleaseNoteSection[];
}

export const RELEASE_NOTES: ReleaseNoteItem[] = [
  {
    version: '2.6.0',
    date: '2026-06-24',
    sections: [
      {
        title: 'New features',
        items: [
          'Added a <b>Notes</b> tab in switch and device settings for free-form device notes.',
          'Device notes are now included in exported documentation: <b>List of Switches</b> and <b>Device IP / VLAN List</b> in Excel and PDF.',
          'Switch-to-switch connections between <b>Fast Ethernet M12 D-coded</b> and <b>Gigabit Ethernet M12 X-coded</b> ports are now supported. A confirmation dialog appears when creating this connection.',
        ],
      },
      {
        title: 'Updates',
        items: [
          'Mixed FE/GE switch links now use the correct connector type on each side.',
          'Mixed cables are now included in the Cable List export for diagrams, Excel, and PDF.',
          'Auto configuration wizard: the same variable name can be reused across fields and applies the same value everywhere (for example <b>192.168.{v1}.{v1}</b>). Use different names when different value ranges are needed.',
        ],
      },
      {
        title: 'Bugfixes',
        items: [
          '<b>Ctrl+Z</b> and <b>Ctrl+Y</b> now work correctly inside text fields and no longer trigger project-level undo/redo.',
          'Auto configuration wizard: newly added variables are validated automatically so export is no longer blocked until Refresh is clicked.',
          'Auto configuration wizard: existing variable options are preserved when variables change; range end is adjusted when the number of export files changes.',
        ],
      },
      {
        title: 'Misc',
        items: [],
      },
    ],
  },
  {
    version: '2.5.0',
    date: '2026-06-16',
    sections: [
      {
        title: 'New features',
        items: [
          'Canvas network topology can be generated by uploading a configuration file (.zip or .cfg)',
          "Device's web interface is available for detailed insight and settings for each ROQSTAR Switch",
        ],
      },
      {
        title: 'Updates',
        items: [
          'Updated images for ROQSTAR Switches',
          'Removed EOL Models 006-130-126 and 006-130-127',
        ],
      },
      {
        title: 'Bugfixes',
        items: [
          'Wrong IP address or subnet values, after importing configuration',
          'PoE documentation issues, when exporting project files',
          'Traffic could be dropped in mixed VLAN setup, when automatic VLAN rules were used',
          'Missed untagged traffic, when multiple VLANs were used',
        ],
      },
      {
        title: 'Misc',
        items: [
          'Exported DHCP lease lists are now following the ports user actually uses',
          'Improved UI for multi-VLAN connections',
          'Warning before losing unsaved changes',
        ],
      },
    ],
  },
  {
    version: '2.4.1',
    date: '2025-03-11',
    changes: [
      'Frame improvements',
      'Selection display: when the user selects the Frame, the area outside it turns monochrome (black and white).',
      'Emphasis color: Frame border turns red when selected to distinguish it from objects inside.',
      'Frame lock: when the Frame is "Locked", the system ignores it so users do not accidentally click the Frame while working with devices inside.',
      'Layering: Frame always appears on top (foreground) when selected for easier identification.',
      'Frame header: automatically updated from Project Data.',
      'Cable & connection configuration',
      'Cable Type UI: removed the add-cable (+) control (each connection has only one cable). Removed row numbering (1, 2, 3…) in the table.',
      'Connector selection: Switch side connector is fixed by port speed (e.g. Gigabit = M12 X-coded, 100M = M12 D-coded). Camera/device side: choose M12 X-coded or RJ45 via dropdown. D-code and X-code cannot be mixed on the same cable.',
      'Power cable: added Power cable configuration tab for all Switches (including Unmanaged), with a "Length" field only.',
      'Data management & export',
      'Project Data: added fields – Project name, Project code, Drawn by, Engineer, Revision, Description.',
      'PDF export All page headers (Diagram, Switch list, Cable list) are consistent and taken from Project Data.',
      'Export: added "Cable List" export to Excel and PDF.',
      'System & UI/UX',
      'Unmanaged Switch: IP settings tab/fields are fully hidden (only Name field remains).',
      'Version & Release Notes: version label in the bottom corner is now a button; click to open a popup with release notes (scrollable for long content).',
      'Device IP addresses: devices are now ordered alphabetically by name with natural number order (e.g. Camera 1, Camera 2, Camera 10).',
      'Export: Pretty Print JSON files (indented with 2 spaces, line endings are \n).',
      'Bug fix: Copy-paste in IP address field (e.g. in Device IP addresses) no longer overwrites read-only octets; only the editable octet(s) are updated when pasting a full IP or a single octet.',
      'Bug fix: Fix issue can not save DHCP server when ip validation failed',
      'Bug fix: Device IP is now validated against the assigned subnet (e.g. 192.168.60.0/23); addresses outside the subnet are rejected.',
      'Bug fix: VLAN subnet mask changes are validated for overlap; e.g. widening a subnet so it overlaps another is now rejected.',
      'Port-based DHCP is now disabled by default when adding a new device.',
    ],
  },
  {
    version: '2.4.0',
    date: undefined,
    changes: ['N/A'],
  },
];

/** Get release notes for a given version. */
export function getReleaseNotesForVersion(
  version: string,
): ReleaseNoteItem | undefined {
  return RELEASE_NOTES.find((n) => n.version === version);
}

/** Latest release notes (first entry in the list). */
export function getLatestReleaseNotes(): ReleaseNoteItem {
  return RELEASE_NOTES[0];
}
